<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.order.actions.addProduct.title')"
    :info="$t('components.order.actions.addProduct.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.order.actions.addProduct.title'), type: 'confirm', callback: confirm, disabled: loadingProducts },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <div class="border rounded-lg h-96 bg-gray-100 dark:bg-gray-900 overflow-hidden flex flex-col">
          <div class="shrink-0 bg-white border-b dark:bg-gray-950 dark:border-gray-800">
            <div class="flex items-center gap-1 pl-2 h-10">
              <div class="relative flex-1 min-w-0">
                <UInput v-model="query" :loading="syncing || loadingProducts" :placeholder="$t('order.filterProducts')" icon="i-lucide-search" variant="none" class="w-full" />
                <div class="absolute right-0 transform mr-2 top-1/2 -translate-y-1/2">
                  <o-button
                    v-if="query.length"
                    size="xs"
                    variant="danger"
                    transparent
                    flat
                    icon="x"
                    @click="query = ''"
                  />
                </div>
              </div>

              <div class="flex items-center gap-1 shrink-0 pr-3">
                <p v-if="summary" class="shrink-0 text-xs text-gray-500 whitespace-nowrap dark:text-gray-400">
                  {{ summary }}
                </p>

                <FacetedFilter v-if="!loadingProducts" :controller="filters" mode="button" />
              </div>
            </div>

            <div
              v-if="!filters.isEmpty.value"
              class="px-2 py-2 border-t dark:border-gray-800"
            >
              <FacetedFilter :controller="filters" mode="chips" />
            </div>
          </div>

          <o-scroll base-class="flex-1 min-h-0 w-full max-w-full">
            <div
              v-if="loadingProducts"
              class="flex h-64 items-center justify-center gap-2 text-sm text-gray-700 dark:text-gray-200"
              role="status"
            >
              <o-loader size="20px" />
              <span>{{ $t('general.loading') }}</span>
            </div>

            <template v-else-if="products">
              <div
                v-if="!visibleCount"
                class="flex h-64 flex-col items-center justify-center gap-3 px-6 text-center text-sm text-gray-600 dark:text-gray-300"
              >
                <p>
                  {{ totalCount
                    ? $t('components.order.actions.addProduct.noMatches')
                    : $t('components.order.actions.addProduct.noProducts') }}
                </p>
                <o-button
                  v-if="isFiltering"
                  size="xs"
                  variant="secondary"
                  @click="resetFilters"
                >
                  {{ $t('general.clearFilters') }}
                </o-button>
              </div>

              <template v-for="productCategory in filtered" :key="productCategory.category ? productCategory.category.idProductCategory : 0">
                <div v-if="productCategory.products.length" class="pb-4 px-4 first:pt-4">
                  <h4 v-if="productCategory.category" class="text-sm text-gray-700 font-medium mb-2 dark:text-gray-200">
                    {{ productCategory.category.name }}
                  </h4>

                  <div class="rounded bg-gray-50 flex flex-col divide-y border dark:bg-gray-900">
                    <div v-for="product in productCategory.products" :key="product.product.idProduct" class="px-3 py-2 text-sm flex justify-between">
                      <div class="flex items-center space-x-2">
                        <o-avatar :items="[ product.product ]" />
                        <div>
                          <p class="text-gray-800 font-medium dark:text-gray-100">
                            {{ product.product.name }}
                          </p>
                          <p v-if="query.length && product.product.category" class="text-gray-500 dark:text-gray-400">
                            {{ product.product.category.name }}
                          </p>
                          <p class="text-gray-600 dark:text-gray-300">
                            {{ product.product.priceLabel }} - {{ $t('components.order.actions.addProduct.inventory', { count: product.product.inventory }) }}
                          </p>
                        </div>
                      </div>

                      <div class="w-16 flex-shrink-0 pl-2">
                        <number-field v-model="product.quantity" slim />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </o-scroll>
        </div>

        <o-field-group :label="$t('general.employee')" class="w-full mt-6 block">
          <employee-select
            v-model="employee"
            :types="['employee', 'admin']"
            class="w-full"
            allow-none
            size="lg"
          />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import Fuse from 'fuse.js'

import { useProductStore } from '~/stores/useProductStore'
import { useProductPickerFilters } from '~/composables/filters/useProductPickerFilters'

import EmployeeSelect from '@/components/employee/elements/Select.vue'
import FacetedFilter from '@/components/elements/facetedFilter/FacetedFilter.vue'
import {
  OAvatar,
} from '@/components/elements'
import {
  NumberField,
} from '@/components/fields'

import type { Product } from '@/_api/models/product'

interface ProductEntry {
  product: Product
  quantity: number
}

interface ProductGroup {
  category: any
  products: ProductEntry[]
}

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const productStore = useProductStore()

const dialog = ref<any>()
const form = ref<any>()

const query = ref('')
const search = ref('')
const products = ref<ProductGroup[] | null>(null)
const employee = ref<any>(null)
const loadingProducts = ref(false)
let latestProductsRequest = 0

const syncing = computed(() => productStore.isSyncing)

const catalogue = computed<Product[]>(() =>
  (products.value ?? []).flatMap(group => group.products.map(entry => entry.product)),
)

const filters = useProductPickerFilters(catalogue)

const debounceSearch = debounce((value: string) => {
  search.value = value
}, 500)

watch(query, (value) => {
  debounceSearch(value)
})

const facetedIds = computed<Set<number> | null>(() => {
  if (!products.value || filters.isEmpty.value) return null

  return new Set(
    filters.filterLocal(catalogue.value).map((product: Product) => product.idProduct),
  )
})

const filtered = computed<ProductGroup[] | null>(() => {
  const groups = products.value

  if (!groups) return null

  const ids = facetedIds.value

  const faceted = ids
    ? groups
      .map(group => ({ ...group, products: group.products.filter(entry => ids.has(entry.product.idProduct)) }))
      .filter(group => group.products.length)
    : groups

  if (!search.value) return faceted

  const entries = faceted.flatMap(group => group.products)
  const fuse = new Fuse(entries, {
    keys: [
      'product.name',
      'product.category.name',
      'product.brand.name',
      'product.barcode',
    ],
  })

  return [
    { category: null, products: fuse.search(search.value).map(({ item }) => item) },
  ]
})

const totalCount = computed(() => catalogue.value.length)

const visibleCount = computed(() =>
  (filtered.value ?? []).reduce((count, group) => count + group.products.length, 0),
)

const selectedCount = computed(() =>
  (products.value ?? []).reduce(
    (count, group) => count + group.products.filter(entry => !!entry.quantity).length,
    0,
  ),
)

const isFiltering = computed(() => !!search.value || !filters.isEmpty.value)

const resetFilters = () => {
  debounceSearch.cancel()
  query.value = ''
  search.value = ''
  filters.clear()
}

const summary = computed(() => {
  if (loadingProducts.value) return null

  if (selectedCount.value) {
    return t('components.order.actions.addProduct.selected', { count: selectedCount.value })
  }

  if (visibleCount.value === totalCount.value) return null

  return t('components.order.actions.addProduct.showing', {
    count: visibleCount.value,
    total: totalCount.value,
  })
})

async function open () {
  const requestId = ++latestProductsRequest

  resetFilters()
  products.value = null
  employee.value = null
  loadingProducts.value = true
  dialog.value.open()

  try {
    await productStore.sync()

    if (requestId !== latestProductsRequest) return

    const list = productStore.all

    products.value = list
      .map(({ category, products }: any): ProductGroup => {
        return {
          category,
          products: products
            .map((product: Product): ProductEntry => {
              return {
                product,
                quantity: 0,
              }
            }),
        }
      })
  } finally {
    if (requestId === latestProductsRequest) {
      loadingProducts.value = false
    }
  }
}

async function confirm () {
  if (loadingProducts.value || !products.value) return false

  emit('update:loading', true)

  try {
    const employeeVal = employee.value

    const quantities = products.value
      .flatMap(group => group.products)
      .filter(({ quantity }) => !!quantity)

    const orderId = String(props.order.idOrder)
    const mappedQuantities = quantities.map((q) => ({ productId: String(q.product.idProduct), quantity: q.quantity }))
    const employeeId = employeeVal?.idEmployee ? String(employeeVal.idEmployee) : undefined
    const response = await $api.order.addProducts(orderId, { quantities: mappedQuantities, employeeId })
    const order = response.data

    emit('update:order', order)

    $notification.toast({
      title: t('components.order.actions.addProduct.success'),
      text: t('components.order.actions.addProduct.successInfo'),
      variant: 'success',
    })

    emit('update:loading', false)

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
